import { object, string } from 'yup';
import { type EditingActionSchemaContext } from './basicSchedule';
import {
  isArrivalBeforeEnd,
  isArrivalNotBeforeStart,
  isEndNotBeforeStart,
} from '../OperatorsAndVehicles/schema';
import i18n from '../../../../i18n';

// El genérico de `object<…>` en yup 1.x es el tipo del CONTEXTO (no el de los
// valores): aquí, si el horario de la básica es editable (revisión 12-oct R2).
export const EditingActionSchema = object<EditingActionSchemaContext>({
  editionPredefine: string().required(() => i18n.t('validation:required')),
  editionLaneCutting: string(),
  editionElementOrder: string().required(() => i18n.t('validation:required')),
  editionCapitol: string().required(() => i18n.t('validation:required')),
  editionSubCapitol: string().required(() => i18n.t('validation:required')),
  editionDescription: string().required(() => i18n.t('validation:required')),
  editionActivity: string().required(() => i18n.t('validation:required')),
  // Operario que registra (quién rellena) — obligatorio (feedback usuario
  // 2026-07-24). Nota: para actuaciones antiguas sin este dato, exigirá
  // rellenarlo al editarlas.
  registeredByUserId: string().required(() => i18n.t('validation:required')),
  editionImages: string().notRequired(),
  // Horario de la Actuació bàsica (2026-10-08): gestión lo corrige en la
  // validación. Misma regla que Operarios y vehículos (Bug #13): el fin no
  // puede ser anterior al inicio. La actuación normal no lleva estas fechas
  // en este formulario (viven en Operarios), así que no le afecta.
  // Solo con el horario EDITABLE (contexto `basicScheduleEditable`, revisión
  // 12-oct R2): con los campos deshabilitados (tablet, Sala, anulada) un fin
  // < inicio que dejó el back no puede bloquear el avance.
  registerEndDate: string()
    .optional()
    .test(
      'end-after-start',
      () => i18n.t('validation:endAfterStart'),
      function (value) {
        const context = this.options.context as
          | EditingActionSchemaContext
          | undefined;
        if (!context?.basicScheduleEditable) return true;
        const { registerInitDate } = this.parent as {
          registerInitDate?: string;
        };
        return isEndNotBeforeStart(value, registerInitDate);
      }
    ),
  // Hora de llegada de la básica (12-oct): la MISMA regla que Operarios de la
  // actuación normal — estrictamente anterior al fin, solo con las dos — y,
  // como la del fin, solo con el horario editable.
  // Y no anterior al inicio (3-nov), también como en Operarios.
  registerArrivalDate: string()
    .nullable()
    .optional()
    .test(
      'arrival-after-start',
      () => i18n.t('validation:arrivalAfterStart'),
      function (value) {
        const context = this.options.context as
          | EditingActionSchemaContext
          | undefined;
        if (!context?.basicScheduleEditable) return true;
        const { registerInitDate } = this.parent as {
          registerInitDate?: string;
        };
        return isArrivalNotBeforeStart(value, registerInitDate);
      }
    )
    .test(
      'arrival-before-end',
      () => i18n.t('validation:arrivalBeforeEnd'),
      function (value) {
        const context = this.options.context as
          | EditingActionSchemaContext
          | undefined;
        if (!context?.basicScheduleEditable) return true;
        const { registerEndDate } = this.parent as {
          registerEndDate?: string;
        };
        return isArrivalBeforeEnd(value, registerEndDate);
      }
    ),
  editionAsset: string().when(
    ['editionAssetSwitch'],
    ([editionAssetSwitch], schema) => {
      return editionAssetSwitch === 'true'
        ? schema.required(() => i18n.t('validation:required'))
        : schema.notRequired();
    }
  ),
});
